import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Switch, TablePagination, Snackbar, Alert, InputAdornment, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import {
  CustomDialog,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../components/customDialog";
import CreateSurveyDialog from './create_survey_dialog';
import SurveyResultsDialog from './survey_results_dialog';
import { makeRequest } from '../../../assets/functions_helper';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { CustomTextField } from '../../../components/textFields';
import { SendButton } from '../../../components/buttons';

export default function MainSurveys({ restaurantId }) {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // Material UI is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterName, setFilterName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal and Snackbar
  const [openCreate, setOpenCreate] = useState(false);
  const [openResults, setOpenResults] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchSurveys = useCallback(() => {
    if (!restaurantId) return;

    setLoading(true);
    // Backward API requires 1-indexed for pages
    let endpoint = `surveys/list/${restaurantId}?page=${page + 1}&size=${rowsPerPage}&order=desc`;
    if (filterName) endpoint += `&filter_name=${encodeURIComponent(filterName)}`;

    makeRequest("GET", endpoint, null, "application/json", localStorage.getItem("token"), localStorage.getItem("tokenType"))
      .then((res) => {
        setSurveys(res.items || []);
        setTotal(res.total || 0);
      })
      .catch((err) => {
        console.error(err);
        showSnackbar("Error al cargar encuestas", "error");
        setSurveys([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [restaurantId, page, rowsPerPage, filterName]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleToggleActive = (surveyId, currentStatus) => {
    const newStatus = !currentStatus;

    // Optistic update
    setSurveys(prev => prev.map(s => {
      if (s.id === surveyId) return { ...s, is_active: newStatus };
      if (newStatus && s.id !== surveyId) return { ...s, is_active: false }; // rule checking
      return s;
    }));

    makeRequest("PUT", `surveys/edit/${surveyId}`, { is_active: newStatus }, "application/json", localStorage.getItem("token"), localStorage.getItem("tokenType"))
      .then(() => {
        showSnackbar(`Encuesta ${newStatus ? 'activada' : 'desactivada'} correctamente`, "success");
        fetchSurveys(); // Sync real state
      })
      .catch((err) => {
        console.error(err);
        showSnackbar("Error al cambiar estado", "error");
        fetchSurveys(); // rollback
      });
  };

  const handleSearchClick = () => {
    setPage(0); // Reset page on new search
    setFilterName(searchInput);
  };

  const handleOpenResults = (id) => {
    setSelectedSurveyId(id);
    setOpenResults(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  return (
    <Box sx={{ p: 4, pt: 1 }}>
      <Typography variant="h5" sx={{ fontFamily: 'Como, sans-serif', fontWeight: 'bold' }} gutterBottom>
        Administración de Encuestas
      </Typography>
      <Typography sx={{ fontFamily: 'Como, sans-serif', color: 'text.secondary', mb: 3 }}>
        Crea y gestiona las preguntas para obtener Retroalimentación de tus clientes al terminar de comer. <br />
        <strong>Importante:</strong> Solo puede existir una encuesta activa a la vez.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <CustomTextField
          variant="outlined"
          size="small"
          placeholder="Buscar encuesta por nombre..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearchClick} edge="end">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 350, backgroundColor: 'white', borderRadius: 1 }}
        />
        <SendButton
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
        >
          Nueva Encuesta
        </SendButton>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 5px 20px rgba(0,0,0,0.06)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#fbfbfb' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#333', textAlign: 'center' }}>Preguntas</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#333', textAlign: 'center' }}>Creación</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#333', textAlign: 'center' }}>Estado (Activo)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#333', textAlign: 'center' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? null : surveys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No se han encontrado encuestas para este restaurante. Usa el botón de Nueva Encuesta para empezar.
                </TableCell>
              </TableRow>
            ) : (
              surveys.map((survey) => (
                <TableRow key={survey.id} hover>
                  <TableCell><strong>{survey.name}</strong></TableCell>
                  <TableCell>{survey.description}</TableCell>
                  <TableCell align="center">{survey.question_count}</TableCell>
                  <TableCell align="center">
                    {new Date(survey.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={Boolean(survey.is_active)}
                      onChange={() => handleToggleActive(survey.id, survey.is_active)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleOpenResults(survey.id)} title="Ver Estadísticas">
                      <AssessmentIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página:"
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Creación Modal Wrapper */}
      <CustomDialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <CustomDialogTitle>Nueva Encuesta</CustomDialogTitle>
        <CustomDialogContent>
          <CreateSurveyDialog
            restaurantId={restaurantId}
            onSuccess={() => {
              setOpenCreate(false);
              setPage(0); // Regresamos a la primer pagina despues de crear
              setSearchInput("");
              setFilterName(""); // Quitamos filtros para que se vea la creada
              fetchSurveys();
            }}
            onClose={() => setOpenCreate(false)}
            showSnackbar={showSnackbar}
          />
        </CustomDialogContent>
      </CustomDialog>

      {/* Resultados Modal Wrapper */}
      <CustomDialog open={openResults} onClose={() => setOpenResults(false)} maxWidth="md" fullWidth>
        <CustomDialogTitle>Análisis de Resultados</CustomDialogTitle>
        <CustomDialogContent dividers>
          {selectedSurveyId && <SurveyResultsDialog surveyId={selectedSurveyId} />}
        </CustomDialogContent>
      </CustomDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
